import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  deriveKeyFromPin,
  decrypt,
  encrypt,
  generateNonce,
  zeroize,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWallet,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { Button } from '@gonka-wallet/ui-tokens';
import { PinPad } from '../components/PinPad.js';
import { useWalletStore } from '../stores/walletStore.js';
import { bytesToHex, hexToBytes } from '../lib/format.js';

type Stage = 'current' | 'new' | 'confirm' | 'done';

function ChangePinNav({ step, onBack, total = 4, t }: { step: number; onBack: () => void; total?: number; t: (key: string) => string }) {
  return (
    <>
      <div className="flex items-center justify-between py-2">
        <button type="button" onClick={onBack} className="text-link text-[15px]">
          ‹ {t('common.back')}
        </button>
        <div className="text-[16px] font-semibold text-text-primary">{t('changePin.navTitle')}</div>
        <div className="text-[11px] text-text-tertiary">{step} of {total}</div>
      </div>
      <div className="flex justify-center gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-0.5 w-7 rounded-full ${
              i === step - 1 ? 'bg-text-primary' : 'bg-surface-3'
            }`}
          />
        ))}
      </div>
    </>
  );
}

export function ChangePin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallets = useWalletStore((s) => s.wallets);
  const setWallets = useWalletStore((s) => s.setWallets);

  const [stage, setStage] = useState<Stage>('current');
  const [decryptedMnemonic, setDecryptedMnemonic] = useState<Uint8Array | null>(
    null,
  );
  const [newPin, setNewPin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shaking, setShaking] = useState(false);

  function triggerShake() {
    setShaking(true);
    setTimeout(() => setShaking(false), 420);
  }

  // Security: if the user navigates away mid-flow (back button, router
  // change, tab close), zeroize any decrypted mnemonic bytes that were
  // stashed in React state between the 'new' and 'confirm' stages.
  // Without this cleanup the bytes would survive in memory until GC,
  // violating spec invariant I2 for the residency window.
  useEffect(() => {
    return () => {
      if (decryptedMnemonic) {
        zeroize(decryptedMnemonic);
      }
    };
  }, [decryptedMnemonic]);

  function handleBack() {
    if (stage === 'current') {
      navigate('/settings');
    } else if (stage === 'new') {
      setStage('current');
    } else if (stage === 'confirm') {
      setStage('new');
      setNewPin(null);
      setError(null);
    } else {
      navigate('/settings');
    }
  }

  async function verifyCurrent(pin: string) {
    setError(null);
    const wallet = wallets[0];
    if (!wallet) {
      navigate('/welcome');
      return;
    }
    let key: Uint8Array = new Uint8Array(0);
    try {
      const salt = hexToBytes(wallet.salt);
      const nonce = hexToBytes(wallet.nonce);
      const ciphertext = hexToBytes(wallet.ciphertext);
      key = await deriveKeyFromPin(pin, salt);
      const plaintext = decrypt(key, nonce, ciphertext);
      setDecryptedMnemonic(plaintext);
      setStage('new');
    } catch {
      setError(t('changePin.wrongCurrent'));
      triggerShake();
    } finally {
      zeroize(key);
    }
  }

  async function rekey(pin: string) {
    const wallet = wallets[0];
    if (!wallet || !decryptedMnemonic) return;
    let key: Uint8Array = new Uint8Array(0);
    try {
      const salt = crypto.getRandomValues(new Uint8Array(16));
      key = await deriveKeyFromPin(pin, salt);
      const nonce = generateNonce();
      const ciphertext = encrypt(key, nonce, decryptedMnemonic);
      const next: EncryptedWalletBlob = {
        ...wallet,
        salt: bytesToHex(salt),
        nonce: bytesToHex(nonce),
        ciphertext: bytesToHex(ciphertext),
      };
      const db = await openWalletDb();
      await saveWallet(db, next);
      const nextList = wallets.map((w) => (w.id === next.id ? next : w));
      setWallets(nextList);
      setStage('done');
    } finally {
      zeroize(key, decryptedMnemonic);
      setDecryptedMnemonic(null);
      setNewPin(null);
    }
  }

  const stageStep = stage === 'current' ? 1 : stage === 'new' ? 2 : stage === 'confirm' ? 3 : 4;

  if (stage === 'done') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <ChangePinNav step={4} onBack={handleBack} t={t} />
        <div className="mt-4 flex flex-1 flex-col gap-3.5">
          <div className="mx-auto mt-auto mb-auto flex flex-col items-center gap-4">
            <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-surface-2 text-[36px] text-pos">
              ✓
            </div>
            <div className="text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('changePin.successLabel')}
              </div>
              <div className="mt-1.5 text-[20px] font-semibold tracking-[-0.5px] text-text-primary">
                {t('changePin.success')}
              </div>
            </div>
            <div className="mt-auto w-full">
              <Button variant="primary" fullWidth onClick={() => navigate('/settings')}>
                {t('changePin.done')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stageConfig = {
    current: {
      label: t('changePin.currentPinLabel'),
      title: t('changePin.navTitle'),
      description: t('changePin.enterCurrentPin'),
      handler: (pin: string) => void verifyCurrent(pin),
    },
    new: {
      label: t('changePin.newPinLabel'),
      title: t('changePin.navTitle'),
      description: t('changePin.new'),
      handler: (pin: string) => {
        setNewPin(pin);
        setStage('confirm');
      },
    },
    confirm: {
      label: t('changePin.confirmPinLabel'),
      title: t('changePin.navTitle'),
      description: t('changePin.confirm'),
      handler: (pin: string) => {
        if (pin !== newPin) {
          setError(t('changePin.mismatch'));
          triggerShake();
          setStage('new');
          setNewPin(null);
          return;
        }
        setError(null);
        void rekey(pin);
      },
    },
  } as const;

  const config = stageConfig[stage as 'current' | 'new' | 'confirm'];

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
      <ChangePinNav step={stageStep} onBack={handleBack} t={t} />
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-6">
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
            {config.label}
          </div>
          <div className="mt-3 text-[22px] font-semibold tracking-[-0.4px] text-text-primary">
            {stage === 'current' ? t('changePin.enterCurrentPin') : stage === 'new' ? t('changePin.createNewPin') : t('changePin.enterItAgain')}
          </div>
          {stage === 'current' && (
            <div className="mt-3 text-[14px] leading-relaxed text-text-secondary">
              {t('changePin.verifyIdentity')}
            </div>
          )}
        </div>

        <PinPad onComplete={config.handler} shake={shaking} />

        {error && (
          <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
        )}
      </div>
    </div>
  );
}
